// Connects the race half to the page: the live socket, the recorded race fetched from public/replays/,
// the source switcher and the broadcast (timeline + 2 s playout clock).
import { Broadcast } from '../race/broadcast';
import { LiveSource } from '../signal/sources/live';
import { ReplaySource } from '../signal/sources/replay';
import { SourceSwitcher } from '../signal/sources/switcher';
import type { Scheduler } from '../signal/sources/types';
import { findMoments, momentSeekMs, type Moments } from './moments';
import type { AppParams } from './params';

/** `scheduler` replaces wall time for the sources, as fixed-step capture mode does.
 *  `onMoments` receives the moments found in the recording once it loads (for the test hook). */
export function startRaceFeed(p: AppParams, scheduler?: Scheduler, onMoments?: (m: Moments, seekMs: number) => void): { broadcast: Broadcast; switcher: SourceSwitcher } {
  if (p.moment) p = { ...p, source: 'replay' }; // a moment is a place in the recording
  const broadcast = new Broadcast(p.source ?? 'live');
  const switcher = new SourceSwitcher({
    mode: p.source,
    scheduler,
    live: () => new LiveSource(),
    replay: async () => {
      const res = await fetch(`${import.meta.env.BASE_URL}replays/${p.replay}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const tsv = await res.text();
      const moments = findMoments(tsv);
      const seekMs = (p.moment ? momentSeekMs(p.moment, moments) : null) ?? p.seek * 1000;
      onMoments?.(moments, seekMs);
      return new ReplaySource(tsv, { seekMs, speed: p.speed }, scheduler);
    },
  });
  switcher.start({
    onFrame: (f, at) => broadcast.push(f, at),
    onSourceChange: (kind, label, rate) => broadcast.setSource(kind, label, rate),
    onStatus: (s) => { broadcast.connection = s; },
  });
  return { broadcast, switcher };
}
